import React from 'react'
import { createPortal } from 'react-dom'
import QRCode from 'qrcode'
import { ApiError, CardCheckout, api } from '../api/client'
import { useApp } from '../context/AppContext'
import { formatPrice } from '../catalog/format'
import { CHECKOUT_POLL_LIMIT_MS, afterCancel, pollCheckout } from '../payments/status'
import { saveErrorMessage, showToast } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import { SheetKeys } from '../payments/idempotency'
import './Sheet.css'
import './PayLinkSheet.css'
import '../screens/settings/Settings.css'
import { Icon } from '../ui/Icon'

// complete: made from her Complete step, so a booked visit is completed first.
export type PayLinkTarget = { appointment_id: string; complete?: boolean } | { client_id: string; package_id: string }

interface PayLinkSheetProps {
  target: PayLinkTarget
  onClose: () => void
  onPaid: () => void
  // The client already has a card saved, so there is nothing to ask.
  clientHasCard?: boolean
}

type Problem = 'not-ready' | 'nothing-to-charge' | 'failed'

// PayLinkSheet makes a card pay link for one appointment or package sale and
// follows it: QR code and Share/Copy while it is open, then Paid, expired or
// cancelled. Closing the sheet leaves the link open (it can be shown again
// from the appointment); only "Cancel link" cancels it.
export default function PayLinkSheet({ target, onClose, onPaid, clientHasCard = false }: PayLinkSheetProps) {
  const { state, dispatch } = useApp()
  const [checkout, setCheckout] = React.useState<CardCheckout | null>(null)
  const [problem, setProblem] = React.useState<Problem | null>(null)
  const [failure, setFailure] = React.useState('')
  const [qr, setQr] = React.useState('')
  const [attempt, setAttempt] = React.useState(0)
  const [cancelling, setCancelling] = React.useState(false)
  const [checking, setChecking] = React.useState(false)
  // Checking stops after a while; each "Check again" starts a new stretch,
  // asking at once.
  const [stoppedChecking, setStoppedChecking] = React.useState(false)
  const [stretch, setStretch] = React.useState(0)
  // Opening says nothing about saving, so a link that is already open keeps
  // its setting (and the link the client may have scanned stays alive). Only
  // her switch sets it: turning it on makes a new link that asks the client to
  // save their card as they pay.
  const saveCardRef = React.useRef<boolean | undefined>(undefined)
  const [cancelError, setCancelError] = React.useState('')
  const paidReported = React.useRef(false)
  // Both the target and the callbacks may be new objects on every parent
  // render; keep the latest in refs so effects only react to what matters.
  const targetRef = React.useRef(target)
  targetRef.current = target
  const onPaidRef = React.useRef(onPaid)
  onPaidRef.current = onPaid
  const inFlight = React.useRef<{ attempt: number; promise: Promise<CardCheckout> } | null>(null)
  const keys = React.useRef(new SheetKeys()).current

  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !cancelling })

  // markPaid runs once per sheet, whichever of create or poll first sees it.
  const apply = React.useCallback(
    (latest: CardCheckout) => {
      setCheckout(latest)
      if (latest.status === 'paid' && !paidReported.current) {
        paidReported.current = true
        showToast(dispatch, 'Card payment received')
        onPaidRef.current()
      }
    },
    [dispatch],
  )

  // Create the link on open (and again on "Make a new link"). The request is
  // shared by attempt number so React's double-mounted effects in development
  // don't create two links.
  React.useEffect(() => {
    let live = true
    setProblem(null)
    setChecking(false)
    setStoppedChecking(false)
    setCheckout(null)
    setQr('')
    if (inFlight.current?.attempt !== attempt) {
      inFlight.current = { attempt, promise: api.createCheckout(targetRef.current, keys.key(`checkout:${attempt}`), saveCardRef.current) }
    }
    inFlight.current.promise
      .then((created) => {
        if (live) apply(created)
      })
      .catch((e) => {
        if (!live) return
        if (e instanceof ApiError && e.problem === 'card-payments-not-ready') setProblem('not-ready')
        else if (e instanceof ApiError && e.problem === 'nothing-to-charge') setProblem('nothing-to-charge')
        else {
          setFailure(saveErrorMessage(e))
          setProblem('failed')
        }
      })
    return () => {
      live = false
    }
  }, [attempt, apply])

  const url = checkout?.url ?? ''
  React.useEffect(() => {
    if (!url) return
    let live = true
    QRCode.toDataURL(url, { margin: 1, width: 280 })
      .then((data) => {
        if (live) setQr(data)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [url])

  const checkoutId = checkout?.id ?? null
  const open = checkout?.status === 'open'
  React.useEffect(() => {
    if (!checkoutId || !open) return
    return pollCheckout(api.getCheckout, checkoutId, apply, { onTimeUp: () => setStoppedChecking(true), now: stretch > 0 })
  }, [checkoutId, open, apply, stretch])

  const checkAgain = () => {
    setStoppedChecking(false)
    setStretch((n) => n + 1)
  }

  const tradingName = state.me?.business?.trading_name ?? ''

  const share = async () => {
    if (!checkout) return
    const title = tradingName ? `Pay ${tradingName}` : 'Pay'
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url: checkout.url, title })
      } catch {
        // dismissed the share sheet
      }
      return
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(checkout.url)}`, '_blank', 'noopener')
  }

  const copy = async () => {
    if (!checkout) return
    try {
      await navigator.clipboard.writeText(checkout.url)
      showToast(dispatch, 'Link copied')
    } catch {
      showToast(dispatch, "Couldn’t copy the link")
    }
  }

  const cancelLink = async () => {
    if (!checkout) return
    setCancelling(true)
    setCancelError('')
    try {
      const returned = await api.cancelCheckout(checkout.id)
      const next = afterCancel(returned.status)
      if (next === 'close') {
        onClose()
        return
      }
      if (next === 'paid') apply(returned)
      else setChecking(true) // polling carries on until the payment settles
    } catch (e) {
      setCancelError(saveErrorMessage(e))
    }
    setCancelling(false)
  }

  const openCardPayments = () => {
    onClose()
    dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'card-payments' })
  }

  let body: React.ReactNode

  if (problem === 'not-ready') {
    body = (
      <div className="paylink" role="alert">
        <p>Set up card payments in Settings → Plan and payments first.</p>
        <button type="button" className="btn btn-primary" onClick={openCardPayments}>
          Go to Payments
        </button>
      </div>
    )
  } else if (problem === 'nothing-to-charge') {
    body = (
      <p className="paylink__status" role="alert">
        Nothing left to pay for this appointment.
      </p>
    )
  } else if (problem === 'failed') {
    body = (
      <div className="paylink" role="alert">
        <p>Couldn’t make a pay link. {failure}</p>
        <button type="button" className="btn btn-secondary btn-compact" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </button>
      </div>
    )
  } else if (!checkout) {
    body = <p className="paylink__status">Making the pay link…</p>
  } else if (checkout.status === 'paid') {
    body = (
      <div className="paylink">
        <p className="paylink__amount">{formatPrice(checkout.amount_pence)}</p>
        <p className="paylink__status paylink__status--paid" role="status">
          Paid <Icon name="check" size={18} />
        </p>
      </div>
    )
  } else if (checkout.status === 'expired' || checkout.status === 'cancelled') {
    body = (
      <div className="paylink">
        <p className="paylink__status" role="status">
          {checkout.status === 'expired' ? 'This link expired' : 'This link was cancelled'}
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setAttempt((n) => n + 1)}>
          Make a new link
        </button>
      </div>
    )
  } else {
    body = (
      <div className="paylink">
        <p className="paylink__amount">{formatPrice(checkout.amount_pence)}</p>
        {qr ? <img className="paylink__qr" src={qr} alt="Pay link code" /> : <p className="paylink__status">Making the code…</p>}
        {stoppedChecking ? (
          <>
            <p className="paylink__status" role="status">
              Stopped checking after {CHECKOUT_POLL_LIMIT_MS / 60000} minutes.
            </p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={checkAgain}>
              Check again
            </button>
          </>
        ) : (
          <p className="paylink__status" role="status">
            {checking ? 'Checking…' : 'Waiting for payment…'}
          </p>
        )}
        {!clientHasCard && (
          <div className="paylink__save">
            <label className="settings-row settings-row--switch">
              <input
                type="checkbox"
                role="switch"
                checked={checkout.save_card}
                disabled={cancelling}
                onChange={(e) => {
                  saveCardRef.current = e.target.checked
                  setAttempt((n) => n + 1)
                }}
              />
              <span>Save card</span>
            </label>
            <p className="field-hint">Ask first. The client sees that their card will be saved.</p>
          </div>
        )}
        <div className="paylink__buttons">
          <button type="button" className="btn btn-secondary btn-compact" onClick={share}>
            Share
          </button>
          <button type="button" className="btn btn-secondary btn-compact" onClick={copy}>
            Copy
          </button>
        </div>
        <button type="button" className="btn btn-quiet btn-compact" onClick={cancelLink} disabled={cancelling}>
          Cancel link
        </button>
        {cancelError && (
          <p className="field-error" role="alert">
            {cancelError}
          </p>
        )}
      </div>
    )
  }

  const sheet = (
    <div
      className="sheet-overlay"
      onClick={(e) => {
        // Portals bubble to the sheet that opened this one; keep the tap here.
        e.stopPropagation()
        if (!cancelling) onClose()
      }}
    >
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label="Pay link" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>Take card payment</h2>
        </div>
        <div className="sheet-body">{body}</div>
        <button type="button" className="btn btn-quiet btn-block" onClick={onClose} disabled={cancelling}>
          {checkout?.status === 'paid' ? 'Done' : 'Close'}
        </button>
      </div>
    </div>
  )

  // Rendered into the app shell like the other sheets so it sits above the
  // tab bar and whichever sheet opened it.
  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
